import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import LogoutIcon from '@mui/icons-material/Logout'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import CssBaseline from '@mui/material/CssBaseline'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Badge from '@mui/material/Badge'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { useAtomValue } from 'jotai'

import maluLogo from '../../../assets/images/malu-white.png'
import { APP_VERSION } from '../../../config/appVersion.config'
import { AppBar, Drawer, DrawerHeader, DrawerVersionFooter, StaffHeaderLogo } from './Drawer.styled'
import { TitleText } from '../../styles/Typography.styled'
import { useAuth } from '../../../hooks/useAuth'
import { useIsMobile } from '../../../hooks/useScreenSize'
import { userAtom } from '../../../stores/userAtom'
import { getUserDisplayName } from '../../../utils/userDisplay.utils'

import type { MenuItem } from '../../../types/menuItem.types'

export default function MiniDrawer({ menuItems, drawerWidth, children }: { menuItems: MenuItem[]; drawerWidth: number; children: React.ReactNode }) {
  const theme = useTheme()
  const [open, setOpen] = React.useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuth()
  const user = useAtomValue(userAtom)
  const userDisplayName = getUserDisplayName(user)
  const isMobile = useIsMobile()
  const shouldShowAccountActions = !(open && isMobile)

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  const getHeaderTitle = () => {
    const currentRoute = menuItems.find((menuItem) => menuItem.url === location.pathname)
    return currentRoute ? currentRoute.label : ''
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* ------------------------------- Header -------------------------------- */}
      <AppBar color="secondary" position="fixed" open={open} drawerWidth={drawerWidth}>
        <Toolbar sx={{ position: 'relative' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={[
              {
                marginRight: 5,
                zIndex: 1,
              },
              open && { display: 'none' },
            ]}
          >
            <MenuIcon />
          </IconButton>
          <TitleText textColor="white" sx={{ zIndex: 1 }}>
            {getHeaderTitle()}
          </TitleText>
          <StaffHeaderLogo tabletUpOnly alt="Malu" src={maluLogo} />
          {shouldShowAccountActions && (
            <Box
              sx={{
                position: 'absolute',
                right: 10,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                maxWidth: { mobile: 150, tablet: 260 },
              }}
            >
              {userDisplayName && (
                <TitleText
                  textColor="white"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: { mobile: 14, tablet: 16 },
                  }}
                >
                  {userDisplayName}
                </TitleText>
              )}
              <LogoutIcon sx={{ cursor: 'pointer', flexShrink: 0 }} onClick={logout} />
            </Box>
          )}
        </Toolbar>
      </AppBar>
      {/* ------------------------------- Drawer -------------------------------- */}
      <Drawer variant="permanent" open={open} drawerWidth={drawerWidth}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>{theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}</IconButton>
        </DrawerHeader>
        <Divider />
        {/* ------------------------------- Menu Items -------------------------------- */}
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {menuItems.map((menuItem) => (
            <ListItem key={menuItem.url} disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={() => navigate(menuItem.url)}
                sx={{
                  justifyContent: open ? 'initial' : 'center',
                  color: location.pathname === menuItem.url ? theme.palette.secondary.main : 'inherit',
                }}
              >
                <ListItemIcon
                  sx={{
                    justifyContent: 'center',
                    mr: open ? 3 : 'auto',
                    '& .menu-item-icon svg': {
                      fill: location.pathname === menuItem.url ? theme.palette.secondary.main : 'inherit',
                    },
                  }}
                >
                  {menuItem.showWarning ? (
                    <Badge
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      badgeContent={<ReportProblemOutlinedIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />}
                    >
                      <Box className="menu-item-icon" sx={{ display: 'inline-flex' }}>
                        {menuItem.icon}
                      </Box>
                    </Badge>
                  ) : (
                    <Box className="menu-item-icon" sx={{ display: 'inline-flex' }}>
                      {menuItem.icon}
                    </Box>
                  )}
                </ListItemIcon>
                <ListItemText primary={menuItem.label} sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {open && <DrawerVersionFooter>v{APP_VERSION}</DrawerVersionFooter>}
      </Drawer>
      {/* ------------------------------- Page Content -------------------------------- */}
      <Box component="main" sx={{ flexGrow: 1, p: 0, height: '100%', overflow: 'auto' }}>
        <Toolbar /> {/* This automatically takes the header's height */}
        {children}
      </Box>
    </Box>
  )
}
